// @view/authentication/CreateAccount.tsx

import React from "react";
import * as Option from "@utils/Option";
import { ok } from "@ps-bridge/ok";
import * as IO from "@ps-bridge/interop/io";
import { makePlanTier } from "@ps-bridge/models/brands/planTier";
import type { CreateAccountViewProps } from "@ps-bridge/contracts/createAccount";
import type { OptionTs } from "@ps-bridge/interop";
import type { PlanTier } from "@ps-bridge/models/clients/organizationSignup";
import { ioEffect } from "@utils/effects/branded";
import * as styles from "@styles/CreateAccount.css";
import EmptyView from "@view/EmptyView";
import CreateAccountPanel, {
  type CreateAccountPlanOption,
} from "@view/panels/authentication/CreateAccount.panel";
import {
  SquareSignupBillingView,
  type BillingAddressField,
  type BillingAddressForm,
} from "@view/payment/SquarePaymentSetup.view";

type CreateAccountPlanTier = OptionTs<PlanTier>;
type CreateAccountSquareBillingOption =
  CreateAccountViewProps extends { readonly squareBilling: infer Value } ? Value : never;
type CreateAccountSquareBillingInput =
  CreateAccountSquareBillingOption extends OptionTs<infer Value> ? Value : never;
type CreateAccountComponentProps = CreateAccountViewProps & {
  allowSquareBillingStubCapture: boolean;
};

const planOptions: Array<CreateAccountPlanOption> = [
  {
    id: makePlanTier("PayAsYouGo"),
    name: "Pay-as-you-go",
    price: "$1.50 / page",
    detail: "No commitment. Audit trail included on every export.",
    highlight: "Flexible for early teams",
  },
  {
    id: makePlanTier("Standard"),
    name: "Standard",
    price: "$50 / month",
    detail: "$1.00 per processed page for recurring packets.",
    highlight: "Best for steady workflows",
  },
  {
    id: makePlanTier("Enterprise"),
    name: "Enterprise",
    price: "$200 / month",
    detail: "$0.75 per processed page plus priority audit-trail support.",
    highlight: "Scale with dedicated support",
  },
];

const isSquarePlan = (planTier: CreateAccountPlanTier): boolean =>
  Option.fold(
    planTier,
    () => false,
    (value) => value === "PayAsYouGo" || value === "Standard" || value === "Enterprise",
  );

const billingAddressFromSquareBilling = (
  squareBilling: CreateAccountSquareBillingOption,
  firstName: string,
  lastName: string,
): BillingAddressForm =>
  Option.fold(
    squareBilling,
    () => ({
      firstName,
      lastName,
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      region: "",
      country: "",
      postalCode: "",
    }),
    (value) => ({
      firstName: value.billingContact.givenName,
      lastName: value.billingContact.familyName,
      phone: value.billingContact.phone,
      addressLine1: value.billingContact.addressLine1,
      addressLine2: Option.fold(value.billingContact.addressLine2, (): string => "", (line): string => line),
      city: value.billingContact.city,
      region: value.billingContact.region,
      country: value.billingContact.country,
      postalCode: value.billingContact.postalCode,
    }),
  );

const hasRequiredSquareBillingFields = (billingAddress: BillingAddressForm): boolean =>
  [
    billingAddress.firstName,
    billingAddress.lastName,
    billingAddress.addressLine1,
    billingAddress.city,
    billingAddress.region,
    billingAddress.postalCode,
  ].every((value) => value.trim().length > 0) && billingAddress.country.trim().length === 2;

const CreateAccountComponent: React.FC<CreateAccountComponentProps> = ({
  mode,
  stepIndex,
  stepNumber,
  stepCount,
  stepLabel,
  stepper,
  formError,
  email,
  firstName,
  lastName,
  password,
  confirmPassword,
  organizationName,
  goals,
  planTier,
  squareBilling,
  billingName,
  billingCard,
  billingExp,
  billingCvc,
  signupComplete,
  allowSquareBillingStubCapture,
  onModeChange,
  onStepNext,
  onStepBack,
  onStepReset,
  onFormErrorChange,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onOrganizationNameChange,
  onGoalsChange,
  onPlanTierChange,
  onSquareBillingChange,
  onBillingNameChange,
  onBillingCardChange,
  onBillingExpChange,
  onBillingCvcChange,
  onOrganizationSignup,
  onProceedToApp,
  onNavigateToLogin,
  onResetToChoice,
  onHandleNext,
  onHandleBack,
  onHandleSubmit,
}) => {
  const isSome = Option.foldFn(() => false, () => true);
  const passwordsMatch =
    password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
  const squarePlanSelected = isSquarePlan(planTier);
  const [billingAddress, setBillingAddress] = React.useState<BillingAddressForm>(() =>
    billingAddressFromSquareBilling(squareBilling, firstName, lastName),
  );
  const [billingError, setBillingError] = React.useState("");
  const [pendingSubmitAfterTokenize, setPendingSubmitAfterTokenize] = React.useState(false);
  const [squareTokenizeRequestNonce, setSquareTokenizeRequestNonce] = React.useState(0);
  const [hasAcceptedBillingConsent, setHasAcceptedBillingConsent] = React.useState(false);

  React.useEffect(() => {
    setBillingAddress(billingAddressFromSquareBilling(squareBilling, firstName, lastName));
  }, [squareBilling, firstName, lastName]);

  React.useEffect(() => {
    if (!(pendingSubmitAfterTokenize && isSome(squareBilling))) {
      return;
    }

    setPendingSubmitAfterTokenize(false);
    IO.run(onHandleSubmit(ok));
  }, [isSome, onHandleSubmit, pendingSubmitAfterTokenize, squareBilling]);

  const toggleGoal = (goal: string) => {
    const updated = goals.includes(goal)
      ? goals.filter((value) => value !== goal)
      : [...goals, goal];
    return onGoalsChange(updated);
  };

  const billingContact = React.useMemo(
    () => ({
      givenName: billingAddress.firstName.trim() || firstName.trim(),
      familyName: billingAddress.lastName.trim() || lastName.trim(),
      email: email.trim(),
      phone: billingAddress.phone.trim(),
      addressLines: [billingAddress.addressLine1.trim(), billingAddress.addressLine2.trim()].filter(Boolean),
      city: billingAddress.city.trim(),
      region: billingAddress.region.trim(),
      country: billingAddress.country.trim(),
      postalCode: billingAddress.postalCode.trim(),
    }),
    [billingAddress, email, firstName, lastName],
  );
  const squareBillingFieldsReady = hasRequiredSquareBillingFields(billingAddress);

  React.useEffect(() => {
    if (!squareBillingFieldsReady) {
      setHasAcceptedBillingConsent(false);
    }
  }, [squareBillingFieldsReady]);

  const onBillingAddressChange = (field: BillingAddressField) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = ({
      country: event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2),
      default: event.target.value,
    } as const)[field === "country" ? "country" : "default"];

    setBillingError("");
    setPendingSubmitAfterTokenize(false);
    setBillingAddress((current) => ({
      ...current,
      [field]: nextValue,
    }));
    IO.run(onSquareBillingChange(Option.none<CreateAccountSquareBillingInput>()));
    return ok;
  };

  const handleSquareBillingChange = (value: CreateAccountSquareBillingOption) =>
    Option.fold(
      value,
      () => {
        setPendingSubmitAfterTokenize(false);
        setBillingError("Complete billing information to continue.");
        return IO.run(onSquareBillingChange(Option.none<CreateAccountSquareBillingInput>()));
      },
      (nextValue) => {
        setBillingError("");
        return IO.run(onSquareBillingChange(Option.some(nextValue)));
      },
    );

  const handleSquareError = (message: string) => {
    setPendingSubmitAfterTokenize(false);
    setBillingError(message);
    IO.run(onSquareBillingChange(Option.none<CreateAccountSquareBillingInput>()));
    return ok;
  };
  const handleBillingConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasAcceptedBillingConsent(event.target.checked);
    return ok;
  };

  const handleNext = onHandleNext(ok);
  const handleBack = onHandleBack(ok);
  const handleSubmit = onHandleSubmit(ok);
  const handleBillingSubmit = ioEffect(() => {
    if (squarePlanSelected && !squareBillingFieldsReady) {
      setBillingError("Complete the required billing fields before loading the secure card form.");
      return ok;
    }

    if (!(squarePlanSelected && !isSome(squareBilling))) {
      IO.run(handleSubmit);
      return ok;
    }

    setBillingError("");
    setPendingSubmitAfterTokenize(true);
    setSquareTokenizeRequestNonce((current) => current + 1);
    return ok;
  });

  const signupPhase = signupComplete ? "complete" : "form";
  const modePhase: "choose" | "org" = mode === "org" ? "org" : "choose";
  const stepPhase =
    stepIndex === 0
      ? "account"
      : stepIndex === 1
        ? "organization"
        : stepIndex === 2
          ? "plan"
          : "billing";
  const passwordBadgePhase = !password && !confirmPassword
    ? "hidden"
    : passwordsMatch
      ? "match"
      : password.length > 0 && confirmPassword.length > 0
        ? "mismatch"
        : "hidden";
  const formErrorPhase = isSome(formError) ? "visible" : "hidden";
  const formErrorMessage = Option.fold(formError, () => "", (message) => message);
  const backPhase = stepIndex === 0 ? "first" : "back";
  const primaryPhase = stepIndex < stepCount - 1 ? "continue" : "submit";
  const isContinueDisabled = stepIndex === 0 && (!password || !confirmPassword || !passwordsMatch);
  const billingPhase = squarePlanSelected ? "square" : "legacy";
  const billingNotice = squarePlanSelected
    ? "Save a payment method now so signup can submit the page-5 billing payload."
    : "This plan continues outside the embedded billing flow.";
  const billingStatus = Option.fold(
    squareBilling,
    () => (squarePlanSelected ? "Payment method not saved yet" : "Not required for this plan"),
    () => "Payment method saved",
  );
  const billingConsentEnabled = squarePlanSelected ? squareBillingFieldsReady : false;
  const isSubmitDisabled = primaryPhase === "submit" && squarePlanSelected
    ? !(billingConsentEnabled && hasAcceptedBillingConsent)
    : false;
  const billingConsentView = {
    hidden: <EmptyView />,
    visible: (
      <label className={styles.consentRow}>
        <input
          className={styles.consentCheckbox}
          type="checkbox"
          checked={hasAcceptedBillingConsent}
          onChange={handleBillingConsentChange}
          disabled={!billingConsentEnabled}
        />
        <span className={styles.consentLabel}>
          I agree to the Terms of Service and Privacy Notice.
        </span>
      </label>
    ),
  } as const;
  const billingView = {
    square: (
      <SquareSignupBillingView
        billingAddress={billingAddress}
        isSubmitting={false}
        errorMessage={billingError}
        onInputChange={onBillingAddressChange}
        onError={handleSquareError}
        billingContact={billingContact}
        onSquareBillingChange={handleSquareBillingChange}
        allowStubCardCapture={allowSquareBillingStubCapture}
        tokenizeRequestNonce={squareTokenizeRequestNonce}
        showCardForm={squareBillingFieldsReady}
      />
    ),
    legacy: <EmptyView />,
  } as const;

  return (
    <CreateAccountPanel
      mode={modePhase}
      stepIndex={stepIndex}
      stepNumber={stepNumber}
      stepCount={stepCount}
      stepLabel={stepLabel}
      stepper={stepper}
      formError={formError}
      email={email}
      firstName={firstName}
      lastName={lastName}
      password={password}
      confirmPassword={confirmPassword}
      organizationName={organizationName}
      goals={goals}
      planTier={planTier}
      billingName={billingName}
      billingCard={billingCard}
      billingExp={billingExp}
      billingCvc={billingCvc}
      billingNotice={billingNotice}
      billingView={billingView[billingPhase]}
      billingStatus={billingStatus}
      consentView={
        billingConsentView[squarePlanSelected && primaryPhase === "submit" ? "visible" : "hidden"]
      }
      signupComplete={signupComplete}
      onModeChange={onModeChange}
      onStepNext={onStepNext}
      onStepBack={onStepBack}
      onStepReset={onStepReset}
      onFormErrorChange={onFormErrorChange}
      onEmailChange={onEmailChange}
      onFirstNameChange={onFirstNameChange}
      onLastNameChange={onLastNameChange}
      onPasswordChange={onPasswordChange}
      onConfirmPasswordChange={onConfirmPasswordChange}
      onOrganizationNameChange={onOrganizationNameChange}
      onGoalsChange={onGoalsChange}
      onPlanTierChange={onPlanTierChange}
      onBillingNameChange={onBillingNameChange}
      onBillingCardChange={onBillingCardChange}
      onBillingExpChange={onBillingExpChange}
      onBillingCvcChange={onBillingCvcChange}
      onOrganizationSignup={onOrganizationSignup}
      onProceedToApp={onProceedToApp}
      onNavigateToLogin={onNavigateToLogin}
      signupPhase={signupPhase}
      modePhase={modePhase}
      stepPhase={stepPhase}
      passwordBadgePhase={passwordBadgePhase}
      formErrorPhase={formErrorPhase}
      backPhase={backPhase}
      primaryPhase={primaryPhase}
      isContinueDisabled={isContinueDisabled}
      isSubmitDisabled={isSubmitDisabled}
      formErrorMessage={formErrorMessage}
      okValue={ok}
      runIO={IO.run}
      planOptions={planOptions}
      onToggleGoal={toggleGoal}
      onResetToChoice={onResetToChoice(ok)}
      onHandleNext={handleNext}
      onHandleBack={handleBack}
      onHandleSubmit={handleBillingSubmit}
    />
  );
};

export default CreateAccountComponent;
